import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Scroll-reveal: sadržaj se pojavi (fade + pomak nagore) kad njegova
// "stranica" uđe u viewport, i ponovo svaki put kad joj se korisnik vrati
// (fullpage osećaj — svaka celina je svoj ekran). CSS klase su u <style> bloku.
function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setOn(entry.isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`bc-reveal${on ? ' on' : ''}`} style={{ transitionDelay: on ? `${delay}ms` : '0ms', ...style }}>
      {children}
    </div>
  );
}

function useMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// Eklipsa — CSS port vizuala iz hb-public-assessment/EclipseIntro.js
// (radijalni glow, dva conic prstena, svetleća tačka, crni disk) bez
// framer-motion zavisnosti; animacije su CSS keyframes ispod.
function Eclipse({ size }) {
  const ringSize = Math.round(size * 0.81);
  const circleSize = Math.round(size * 0.8);
  const dotRight = Math.round(size * 0.085);
  const dotSize = size < 300 ? 6 : 8;
  const beamWidth = Math.round(size * 0.57);
  const beamRight = -Math.round(size * 0.19);
  return (
    <div style={{
      position: 'relative', width: size, height: size,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {/* Pulsirajuci topli glow */}
      <div className="bc-eclipse-glow" style={{
        position: 'absolute', width: '110%', height: '110%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 180, 120, 0.12) 0%, rgba(255, 100, 50, 0) 70%)',
        filter: 'blur(40px)', zIndex: 1,
      }} />
      {/* Svetlosni zrak (romb) desno */}
      <div style={{
        position: 'absolute', width: beamWidth, height: 5,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.9) 50%, transparent)',
        clipPath: 'polygon(0% 50%, 50% 0%, 100% 50%, 50% 100%)',
        filter: 'blur(1px)', zIndex: 7, right: beamRight, top: '51%',
        transform: 'translateY(-50%)', pointerEvents: 'none',
      }} />
      {/* Dva conic prstena */}
      <div style={{
        position: 'absolute', width: ringSize, height: ringSize, borderRadius: '50%',
        background: 'conic-gradient(from 260deg at 50% 50%, transparent 0%, rgba(255,255,255,0.8) 15%, transparent 30%)',
        filter: 'blur(2px)', transform: 'rotate(80deg)', zIndex: 4,
      }} />
      <div style={{
        position: 'absolute', width: ringSize, height: ringSize, borderRadius: '50%',
        background: 'conic-gradient(from 80deg at 50% 50%, transparent 0%, rgba(255,255,255,0.8) 15%, transparent 30%)',
        filter: 'blur(2px)', transform: 'rotate(80deg)', zIndex: 4,
      }} />
      {/* Svetleca tacka */}
      <div className="bc-eclipse-dot" style={{
        position: 'absolute', right: dotRight, top: '51%',
        width: dotSize, height: dotSize, backgroundColor: '#fff', borderRadius: '50%',
        zIndex: 10, boxShadow: '0 0 25px 8px rgba(255,255,255,0.9)',
      }} />
      {/* Crni disk */}
      <div style={{
        position: 'relative', width: circleSize, height: circleSize,
        backgroundColor: '#000', borderRadius: '50%', zIndex: 5,
        boxShadow: '0 0 15px rgba(0,0,0,1)',
      }} />
    </div>
  );
}

// Public entry page — basecamp.hansenbeck.com
// Sadržaj i redosled prate mockup landing: golden circle — why (1911 priča)
// prvo, kako radi drugo, poziv ("Become an Ice Pilot / Business Partner") na kraju.
// "Start the conversation" otvara mail ka MD adresi (open decision register #8 —
// adresa preuzeta iz mockup-a).

const MD_MAIL = 'nikola.mircic@hansenbeck.com';

function Kick({ children }) {
  return <div style={{ fontSize: '0.7rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 700, marginBottom: 12 }}>{children}</div>;
}

// HB fineline ilustracija — SVG sa transparentnom pozadinom i tamnim linijama;
// brightness(0) invert(1) ih pretvara u čiste bele linije za crnu pozadinu.
function Illustration({ src, isMobile, width }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{
        width: width || (isMobile ? 150 : 250), height: 'auto', flexShrink: 0,
        filter: 'brightness(0) invert(1)', opacity: 0.82,
        pointerEvents: 'none',
      }}
    />
  );
}

// Svaka sekcija je svoja "stranica": ceo ekran, snap na skrol, sadržaj
// vertikalno centriran i pojavi se tek kad se doskroluje do nje. Uz opcionu
// ilustraciju sekcija postaje dvokolonska (naizmenično strana preko `flip`).
function Section({ id, kick, title, illustration, flip, children }) {
  const isMobile = useMobile();
  return (
    <div id={id} style={{
      minHeight: '100vh', scrollSnapAlign: 'start', scrollSnapStop: 'always',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: '48px 24px',
    }}>
      <div style={{
        maxWidth: illustration ? 1040 : 820, margin: '0 auto', width: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : (flip ? 'row-reverse' : 'row'),
        alignItems: 'center', gap: isMobile ? 30 : 60,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Reveal>
            <Kick>{kick}</Kick>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: '#fff', lineHeight: 1.2, marginBottom: 18 }}>{title}</h2>
          </Reveal>
          <Reveal delay={160}>{children}</Reveal>
        </div>
        {illustration && (
          <Reveal delay={260} style={{ flexShrink: 0, alignSelf: 'center' }}>
            <Illustration src={illustration} isMobile={isMobile} />
          </Reveal>
        )}
      </div>
    </div>
  );
}

const pStyle = { color: 'rgba(255,255,255,0.65)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 14 };

function Steps({ steps }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 18, marginTop: 26 }}>
      {steps.map((s, i) => (
        <Reveal key={i} delay={i * 110}>
          <div style={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 'var(--radius-md)', padding: '18px 16px', height: '100%' }}>
            {s.no && <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.85rem', color: 'rgba(255,255,255,0.35)', marginBottom: 6 }}>{s.no}</div>}
            <div style={{ fontFamily: 'var(--font-display)', fontSize: s.big ? '1.9rem' : '1.05rem', color: '#fff', marginBottom: 6 }}>{s.title}</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>{s.text}</div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

// Vertikalne tačkice desno — indikator "ima još dole" + brza navigacija.
// Prati koja je sekcija u fokusu (IntersectionObserver) i puni aktivnu tačku;
// sakriva se dok si na hero-u (pojavljuje se od druge sekcije naniže).
function ScrollDots({ sections }) {
  const [active, setActive] = useState('hero');
  useEffect(() => {
    // Sekcija koja pređe preko pola ekrana postaje aktivna (uklj. hero, čime
    // se tačkice sakriju na vrhu). IntersectionObserver root=viewport.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) setActive(e.target.id);
        });
      },
      { threshold: [0.5] }
    );
    const els = sections.map(s => document.getElementById(s.id)).filter(Boolean);
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [sections]);

  const onHero = active === 'hero';
  const dots = sections.filter(s => s.id !== 'hero');
  return (
    <div style={{
      position: 'fixed', right: 24, top: '50%', transform: 'translateY(-50%)', zIndex: 60,
      display: 'flex', flexDirection: 'column', gap: 14,
      opacity: onHero ? 0 : 1, transition: 'opacity 0.45s ease',
      pointerEvents: onHero ? 'none' : 'auto',
    }}>
      {dots.map(s => {
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            aria-label={s.label}
            title={s.label}
            onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              width: 11, height: 11, borderRadius: '50%', padding: 0, cursor: 'pointer',
              border: '1.5px solid rgba(255,255,255,0.55)',
              background: isActive ? '#fff' : 'transparent',
              transform: isActive ? 'scale(1.2)' : 'scale(1)',
              transition: 'background 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
              borderColor: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
            }}
          />
        );
      })}
    </div>
  );
}

function Cta({ href, children, ghost }) {
  return (
    <a href={href} style={{
      display: 'inline-block', marginTop: 22, marginRight: 12, padding: '12px 26px',
      borderRadius: 'var(--radius-md)', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.02em',
      background: ghost ? 'transparent' : '#fff', color: ghost ? '#fff' : '#000',
      border: ghost ? '1px solid rgba(255,255,255,0.4)' : 'none', textDecoration: 'none',
    }}>{children}</a>
  );
}

function JoinSection() {
  const [activeTab, setActiveTab] = useState('ip'); // 'ip' ili 'bp'
  const isMobile = useMobile();

  return (
    <div id="join" style={{
      minHeight: '100vh', scrollSnapAlign: 'start', scrollSnapStop: 'always',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: isMobile ? '80px 24px 40px' : '60px 24px 40px', boxSizing: 'border-box'
    }}>
      <div style={{ maxWidth: 920, margin: '0 auto', width: '100%' }}>
        <Reveal>
          <Kick>Join Hansen Beck</Kick>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? '1.8rem' : '2.2rem', color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>
            If this page felt like a mirror — read on.
          </h2>
        </Reveal>

        {/* Tab dugmad za izbor staze */}
        <Reveal delay={120}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, margin: '24px 0 28px' }}>
            <button
              onClick={() => setActiveTab('ip')}
              style={{
                textAlign: 'left', padding: '16px 20px', cursor: 'pointer', borderRadius: 'var(--radius-md)',
                background: activeTab === 'ip' ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: `1px solid ${activeTab === 'ip' ? '#fff' : 'rgba(255,255,255,0.2)'}`,
                color: '#fff', transition: 'all 0.25s ease',
              }}
            >
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontWeight: 700 }}>Option 1</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600 }}>Become an Ice Pilot</div>
            </button>

            <button
              onClick={() => setActiveTab('bp')}
              style={{
                textAlign: 'left', padding: '16px 20px', cursor: 'pointer', borderRadius: 'var(--radius-md)',
                background: activeTab === 'bp' ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: `1px solid ${activeTab === 'bp' ? '#fff' : 'rgba(255,255,255,0.2)'}`,
                color: '#fff', transition: 'all 0.25s ease',
              }}
            >
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4, fontWeight: 700 }}>Option 2</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600 }}>Develop a Market (Business Partner)</div>
            </button>
          </div>
        </Reveal>

        {/* Tab 1: Become an Ice Pilot */}
        {activeTab === 'ip' && (
          <Reveal key="tab-ip">
            <div>
              <p style={{ ...pStyle, fontSize: '0.95rem' }}>
                Becoming an Ice Pilot is not a career move; it is a change of profession into one with a dual mastery —
                winning clients as a trusted consultant and changing behaviour in the training room. We invest in people
                who bring the mindset; the craft we build together, deliberately, the same way we develop everyone else.
                From first conversation to your first delivered programme: one year.
              </p>
              <Steps steps={[
                { no: '1', title: 'Pre-selection', text: 'A structured conversation about mindset, communication and drive — both sides check the fit.' },
                { no: '2', title: 'Orientation', text: 'Deep conversations about the role, expectations and the Being an Ice Pilot philosophy.' },
                { no: '3', title: 'Formation (CFA)', text: 'Candidates Formation & Assessment — structured development, with your own Basecamp access from day one.' },
                { no: '4', title: 'Certification', text: 'Ice Pilot Explorer — the licence to sell and deliver under the Hansen Beck brand.' },
              ]} />
              <Cta href={`mailto:${MD_MAIL}?subject=I%20want%20to%20become%20an%20Ice%20Pilot`}>Start the conversation</Cta>
            </div>
          </Reveal>
        )}

        {/* Tab 2: Become a Business Partner */}
        {activeTab === 'bp' && (
          <Reveal key="tab-bp">
            <div>
              <p style={{ ...pStyle, fontSize: '0.95rem' }}>
                Some people read the why above and see a profession. Others see a market. Business Partners are
                entrepreneurs who believe their territory deserves development done properly — and who want to build a
                company on that belief, without inventing everything from zero.
              </p>
              <p style={{ ...pStyle, fontSize: '0.95rem' }}>
                How it works: you hold exclusive rights to a defined territory through a Cooperation Agreement with
                Hansen Beck GmbH. You build the local business — winning corporate clients, recruiting and developing
                your own Ice Pilots. Hansen Beck stands behind you with the brand, the complete content system, the
                HB Compass methodology and platform, support in selecting and forming your trainers, an annual business
                dialogue with the Managing Directors as a partner — not an inspector — and a partner community that
                shares what works across markets. The path: a conversation about your market → a business plan we shape
                together → the Cooperation Agreement → building your team.
              </p>
              <Cta href={`mailto:${MD_MAIL}?subject=Business%20Partner%20%E2%80%93%20territory%20conversation`}>Start the territory conversation</Cta>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}

export default function EntryPage() {
  const isMobile = useMobile();
  return (
    <div className="bc-snap" style={{
      // Fullpage snap: svaka sekcija se otvori cela. scroll-snap-stop:always
      // (na sekcijama) garantuje jedan pokret = jedna sekcija. Ključno: nijedna
      // sekcija ne sme biti viša od ekrana (spojena je stisnuta da stane) —
      // inače mandatory pravi dupli/poluskrol.
      background: '#000', height: '100vh', overflowY: 'auto',
      scrollSnapType: 'y mandatory', scrollBehavior: 'smooth',
    }}>
      <style>{`
        @keyframes bcEclipsePulse { 0%,100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.05); } }
        @keyframes bcDotPulse { 0%,100% { opacity: 0.8; transform: translateY(-50%) scale(1); } 50% { opacity: 1; transform: translateY(-50%) scale(1.2); } }
        @keyframes bcArrowBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(9px); } }
        .bc-eclipse-glow { animation: bcEclipsePulse 6s ease-in-out infinite; }
        .bc-eclipse-dot { animation: bcDotPulse 3s ease-in-out infinite; }
        .bc-scroll-arrow { animation: bcArrowBounce 1.8s ease-in-out infinite; }
        .bc-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .bc-reveal.on { opacity: 1; transform: none; }
        @media (prefers-reduced-motion: reduce) { .bc-reveal { opacity: 1; transform: none; transition: none; } }
      `}</style>

      <ScrollDots sections={[
        { id: 'hero', label: 'Top' },
        { id: 'intro', label: 'Welcome' },
        { id: 'why', label: 'Our story' },
        { id: 'believe', label: 'What we believe' },
        { id: 'how', label: 'How it works' },
        { id: 'join', label: 'Join Hansen Beck' },
      ]} />

      {/* Header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '30px 24px' : '42px 52px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 26, minWidth: 0, flex: 1, marginRight: 16 }}>
          <img src="/whitetransparent.png" alt="Hansen Beck" style={{ height: isMobile ? 52 : 78, flexShrink: 0 }} />
          <div style={{
            fontSize: isMobile ? '0.72rem' : '0.95rem', letterSpacing: '0.32em',
            color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
          }}>
            Explore. Master. Deliver.
          </div>
        </div>
        <Link to="/login" style={{
          padding: '8px 18px', borderRadius: 'var(--radius-sm)', fontSize: '0.83rem',
          color: '#000', background: '#fff', fontWeight: 600, letterSpacing: '0.02em', flexShrink: 0,
        }}>Sign In</Link>
      </div>

      {/* Hero — pun ekran: samo eklipsa + naslov + strelica */}
      <div id="hero" style={{
        minHeight: '100vh', position: 'relative', scrollSnapAlign: 'start', scrollSnapStop: 'always',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '0 16px', overflow: 'hidden',
      }}>
        <Eclipse size={isMobile ? 250 : 380} />
        <div style={{ textAlign: 'center', zIndex: 20, marginTop: isMobile ? -30 : -50 }}>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontWeight: 900,
            fontSize: isMobile ? '2.2rem' : 'clamp(2.8rem, 7vw, 4.6rem)',
            color: '#fff', lineHeight: 1.05, letterSpacing: isMobile ? '-1px' : '-2px', margin: 0,
          }}>
            Welcome to the<br />Hansen Beck <span style={{ color: 'rgba(255,255,255,0.4)' }}>Basecamp.</span>
          </h1>
        </div>
        {/* Strelica — ima jos sadrzaja dole */}
        <a href="#intro" aria-label="Scroll down" className="bc-scroll-arrow" style={{
          position: 'absolute', bottom: 28, left: '50%', marginLeft: -14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, zIndex: 20,
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9l8 8 8-8" />
          </svg>
        </a>
      </div>

      {/* Intro tekst + izbor — prva "stranica" ispod hero-a */}
      <div id="intro" style={{
        minHeight: '100vh', scrollSnapAlign: 'start', scrollSnapStop: 'always',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '48px 24px',
      }}>
      <div style={{ maxWidth: 820, margin: '0 auto', width: '100%' }}>
        <Reveal>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontWeight: 900,
            fontSize: 'clamp(2.4rem, 6vw, 3.6rem)', color: '#fff', lineHeight: 1.12, marginBottom: 18,
          }}>
            Great Conquests<br />Are Won in the <span style={{ color: 'rgba(255,255,255,0.4)' }}>Backseat.</span>
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p style={{ ...pStyle, fontSize: '1.15rem', maxWidth: 620 }}>
            Base Camp is where expeditions are prepared. This is the world of Hansen Beck —
            for those who are already in it, and for those who feel they might belong here.
          </p>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, maxWidth: 700, marginTop: 30 }}>
          <Reveal delay={220}>
            <Link to="/login" style={{ border: '1px solid rgba(255,255,255,0.18)', borderRadius: 'var(--radius-md)', padding: '22px 20px', display: 'block', height: '100%' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: '#fff', marginBottom: 8 }}>I am a member</div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                Ice Pilots, Business Partners, candidates and shareholders — your workspace, your content, your community.
              </div>
              <div style={{ color: '#fff', fontSize: '0.85rem', marginTop: 12, fontWeight: 600 }}>Enter Basecamp →</div>
            </Link>
          </Reveal>
          <Reveal delay={340}>
            <a href="#why" style={{ border: '1px solid rgba(255,255,255,0.18)', borderRadius: 'var(--radius-md)', padding: '22px 20px', display: 'block', height: '100%' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: '#fff', marginBottom: 8 }}>I am interested in joining HB community</div>
              <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                As a trainer, business partner, or candidate — there's a place for you here. Start where everything starts — with why.
              </div>
              <div style={{ color: '#fff', fontSize: '0.85rem', marginTop: 12, fontWeight: 600 }}>Why HansenBeck →</div>
            </a>
          </Reveal>
        </div>
      </div>
      </div>

      {/* Our story */}
      <Section id="why" kick="Our story" title="December 14, 1911.">
        <p style={pStyle}>
          A race to conquer the South Pole captivated the world. Roald Amundsen won it — but he wasn't alone.
          Ludvig Hansen and Andreas Beck, two forgotten ice pilots, steered the expedition safely through the ice.
        </p>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', color: '#fff', lineHeight: 1.5, margin: '20px 0' }}>
          History remembers the man who planted the flag.<br />We work for the ones who made it possible.
        </p>
        <p style={pStyle}>
          Hansen Beck is an international training company focused on the development of people. Like the ice
          pilots we are named after, we travel with you — uncovering strengths and potentials, building skills,
          and delivering outstanding results. We are the invisible compass.
        </p>
        <Steps steps={[
          { big: true, title: '22', text: 'countries' },
          { big: true, title: '5', text: 'continents' },
          { big: true, title: '1963', text: 'roots — established as Hansen Beck in 2019' },
          { big: true, title: '5,000+', text: 'professionals developed every year' },
        ]} />
      </Section>

      {/* What we believe + Why people join us — dva bloka na jednom ekranu.
          justifyContent: center + veliki gornji padding da ne pređe preko
          navbara; manje ilustracije da oba bloka stanu i budu vidljiva. */}
      <div id="believe" style={{
        minHeight: '100vh', boxSizing: 'border-box', scrollSnapAlign: 'start', scrollSnapStop: 'always',
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        gap: isMobile ? 44 : 8,
        padding: isMobile ? '104px 24px 56px' : '100px 24px 56px',
      }}>
        <div style={{
          maxWidth: 1040, margin: '0 auto', width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row-reverse',
          alignItems: 'center', gap: isMobile ? 24 : 56,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Reveal>
              <Kick>What we believe</Kick>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? '1.7rem' : '1.9rem', color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>The gap between competent and unforgettable is trainable.</h2>
            </Reveal>
            <Reveal delay={160}>
              <p style={{ ...pStyle, fontSize: '0.95rem', marginBottom: 0 }}>
                Two people walk into the same room with the same preparation, the same knowledge, the same technique.
                One wins the room. The other doesn't. The difference is almost never in what they said. That gap is
                smaller than you think — and it is trainable: deliberately, over months, against a precise picture of
                excellence rather than a generic average. Great conquests are prepared quietly, in the backseat, long
                before anyone sees the victory.
              </p>
            </Reveal>
          </div>
          <Reveal delay={260} style={{ flexShrink: 0, alignSelf: 'center' }}>
            <Illustration src="/illustrations/journey-of-change.svg" isMobile={isMobile} width={isMobile ? 110 : 150} />
          </Reveal>
        </div>

        <div style={{
          maxWidth: 1040, margin: '0 auto', width: '100%',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center', gap: isMobile ? 24 : 56,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Reveal>
              <Kick>Why people join us</Kick>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? '1.7rem' : '1.9rem', color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>A business of your own. A rope team behind you.</h2>
            </Reveal>
            <Reveal delay={160}>
              <p style={{ ...pStyle, fontSize: '0.95rem', marginBottom: 0 }}>
                There is a rare kind of profession where your success and other people's growth are the same work.
                Our trainers — we call them Ice Pilots — build their own client base and their own income, on their
                own name. But they are never alone on the mountain: behind every Ice Pilot stands proven content built
                over decades, a methodology that turns intuition into precision, and a community that gathers twice a
                year on Expeditions and between them on Hikes — to prepare together. Freedom of your own business.
                Meaning in what the business does. Mastery as the standard.
              </p>
            </Reveal>
          </div>
          <Reveal delay={260} style={{ flexShrink: 0, alignSelf: 'center' }}>
            <Illustration src="/illustrations/people-development.svg" isMobile={isMobile} width={isMobile ? 110 : 150} />
          </Reveal>
        </div>
      </div>

      {/* How it works */}
      <Section id="how" kick="How it works" title="Explore. Master. Deliver." illustration="/illustrations/compass.svg" flip>
        <Steps steps={[
          { no: '1', title: 'Explore', text: "Win trust as a consultant — understanding what a customer's organisation truly needs before ever naming a solution." },
          { no: '2', title: 'Master', text: "Translate those needs with HB Compass — our common language between the customer's world and our content — into a development programme with a precise target." },
          { no: '3', title: 'Deliver', text: 'Change behaviour in the training room — programmes run in 3–4 week rhythms over months, because that is how long new behaviour takes to hold.' },
        ]} />
      </Section>

      {/* Nova spojena sekcija (Become an Ice Pilot / Business Partner) */}
      <JoinSection />

      {/* Footer — lepi se uz dno poslednje stranice */}
      <div style={{
        textAlign: 'center', padding: '60px 24px 40px', scrollSnapAlign: 'end',
        fontSize: '0.72rem', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase',
      }}>
        Explore. Master. Deliver. &nbsp;·&nbsp; Hansen Beck © 2026
      </div>
    </div>
  );
}
